import { useCallback, useEffect, useRef, useState } from 'react'

type UseAutoSaveMetaOptions<F extends Record<string, string>> = {
  id: string | undefined
  initialFields: F
  saveFn: (id: string, fields: F) => Promise<unknown>
  onSaveSuccess?: (fields: F) => void
  onError?: (message: string) => void
  delay?: number
}

type UseAutoSaveMetaResult<F extends Record<string, string>> = {
  fields: F
  isSaving: boolean
  hasChanges: boolean
  handleFieldChange: (key: keyof F, value: string) => void
  handleManualSave: () => void
}

export function useAutoSaveMeta<F extends Record<string, string>>({
  id,
  initialFields,
  saveFn,
  onSaveSuccess,
  onError,
  delay = 2000,
}: UseAutoSaveMetaOptions<F>): UseAutoSaveMetaResult<F> {
  let [fields, setFields] = useState<F>(initialFields)
  let [lastSavedFields, setLastSavedFields] = useState<F>(initialFields)
  let [isSaving, setIsSaving] = useState(false)
  let [synced, setSynced] = useState(false)

  let fieldsRef = useRef<F>(initialFields)
  let autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  let hasChanges = Object.keys(fields).some(
    (k) => fields[k].trim() !== lastSavedFields[k],
  )

  // Keep ref current so the timer callback always reads the latest values
  useEffect(() => {
    // eslint-disable-next-line fp/no-mutation
    fieldsRef.current = fields
  }, [fields])

  // Clear timer on unmount
  useEffect(
    () => () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    },
    [],
  )

  // On id change: cancel any pending timer and reset all field state so stale
  // values from the previous item cannot be displayed or accidentally saved
  useEffect(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    setFields(initialFields)
    setLastSavedFields(initialFields)
    // eslint-disable-next-line fp/no-mutation
    fieldsRef.current = initialFields
    setSynced(false)
  }, [id]) // intentionally omits initialFields — we want the snapshot at id-change time

  // Sync fields once initial data is available (first render may be before data loads)
  useEffect(() => {
    if (!synced && Object.values(initialFields).some((v) => v !== '')) {
      setFields(initialFields)
      setLastSavedFields(initialFields)
      setSynced(true)
      // eslint-disable-next-line fp/no-mutation
      fieldsRef.current = initialFields
    }
  }, [initialFields, synced])

  let saveMeta = useCallback(() => {
    if (!id || !fieldsRef.current.name?.trim()) return
    let savedFields = Object.fromEntries(
      Object.entries(fieldsRef.current).map(([k, v]) => [k, v.trim()]),
    ) as F
    setIsSaving(true)
    saveFn(id, savedFields)
      .then(() => {
        setLastSavedFields(savedFields)
        if (onSaveSuccess) onSaveSuccess(savedFields)
      })
      .catch(() => {
        if (onError) onError('Failed to save')
      })
      .finally(() => setIsSaving(false))
  }, [id, saveFn, onSaveSuccess, onError])

  let scheduleAutoSave = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    // eslint-disable-next-line fp/no-mutation
    autoSaveTimerRef.current = setTimeout(saveMeta, delay)
  }, [saveMeta, delay])

  let handleFieldChange = useCallback(
    (key: keyof F, value: string) => {
      setFields((prev) => ({ ...prev, [key]: value }))
      scheduleAutoSave()
    },
    [scheduleAutoSave],
  )

  let handleManualSave = useCallback(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    saveMeta()
  }, [saveMeta])

  return { fields, isSaving, hasChanges, handleFieldChange, handleManualSave }
}
